<template>
  <transition name="banner-slide">
    <div
      v-if="announcement"
      :key="announcement.id"
      class="global-announcement-banner"
      :class="[announcement.type, { 'with-header': withHeader }]"
      role="region"
      :aria-label="announcement.title || 'Systemmitteilung'"
    >
      <div class="announcement-banner-content">
        <!-- Type Icon -->
        <div class="announcement-icon" aria-hidden="true">
          <svg v-if="announcement.type === 'alert'" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2L1 21h22L12 2zm1 14h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
          </svg>
          <svg v-else-if="announcement.type === 'warning'" viewBox="0 0 24 24" fill="currentColor">
            <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
          </svg>
          <svg v-else-if="announcement.type === 'success'" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
          <svg v-else viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
          </svg>
        </div>

        <!-- Text Content -->
        <div class="announcement-text">
          <strong v-if="announcement.title" class="announcement-title">
            {{ announcement.title }}:
          </strong>
          <span class="announcement-message">
            {{ announcement.message }}
          </span>
        </div>

        <!-- Optional Action Link / Button -->
        <button
          v-if="announcement.actionLabel && announcement.actionUrl"
          type="button"
          class="announcement-action-btn"
          @click="handleActionClick"
        >
          <span>{{ announcement.actionLabel }}</span>
          <svg viewBox="0 0 24 24" fill="currentColor" class="action-arrow">
            <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
          </svg>
        </button>

        <!-- Dismiss Button -->
        <button
          v-if="announcement.dismissible !== false"
          type="button"
          class="announcement-dismiss-btn"
          @click="handleDismiss"
          aria-label="Mitteilung ausblenden"
          title="Ausblenden"
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAnnouncements } from '../../composables/useAnnouncements'

const props = withDefaults(
  defineProps<{
    withHeader?: boolean
  }>(),
  {
    withHeader: false
  }
)

const router = useRouter()
const { currentBannerAnnouncement, dismissAnnouncement } = useAnnouncements()

const announcement = computed(() => currentBannerAnnouncement.value)

const handleDismiss = () => {
  if (announcement.value) {
    dismissAnnouncement(announcement.value.id)
  }
}

const handleActionClick = () => {
  if (!announcement.value?.actionUrl) return
  const url = announcement.value.actionUrl.trim()

  if (url.startsWith('http://') || url.startsWith('https://')) {
    window.open(url, '_blank', 'noopener,noreferrer')
  } else {
    try {
      const res = router.push(url)
      if (res && typeof (res as any).catch === 'function') {
        ;(res as any).catch(() => {})
      }
    } catch {}
  }
}
</script>

<style scoped>
.global-announcement-banner {
  width: 100%;
  color: #ffffff;
  padding: 8px 16px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  position: relative;
  z-index: 24;
  flex-shrink: 0;
  box-sizing: border-box;
}

.global-announcement-banner.with-header {
  margin-top: 56px;
}

/* Background Types */
.global-announcement-banner.info {
  background: #1e40af; /* Deep accessible blue */
}

.global-announcement-banner.warning {
  background: #b45309; /* Warm visible amber */
}

.global-announcement-banner.alert {
  background: #b91c1c; /* Prominent red */
}

.global-announcement-banner.success {
  background: #15803d; /* Fresh emerald */
}

.announcement-banner-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.announcement-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.announcement-icon svg {
  width: 20px;
  height: 20px;
}

.announcement-text {
  flex: 1;
  font-size: 0.88rem;
  line-height: 1.35;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.35rem;
}

.announcement-title {
  font-weight: 700;
}

.announcement-message {
  font-weight: 500;
}

.announcement-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  background: rgba(255, 255, 255, 0.22);
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.35);
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  user-select: none;
  -webkit-user-select: none;
  -webkit-tap-highlight-color: transparent;
  transition: transform 0.15s ease;
}

@media (hover: hover) and (pointer: fine) {
  .announcement-action-btn:hover {
    background: rgba(255, 255, 255, 0.32);
    transform: translateY(-1px);
  }
}

.announcement-action-btn:active {
  transform: translateY(1px);
}

.action-arrow {
  width: 14px;
  height: 14px;
}

.announcement-dismiss-btn {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.85);
  padding: 4px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  user-select: none;
  -webkit-user-select: none;
  -webkit-tap-highlight-color: transparent;
}

@media (hover: hover) and (pointer: fine) {
  .announcement-dismiss-btn:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.2);
  }
}

.announcement-dismiss-btn svg {
  width: 18px;
  height: 18px;
}

/* Animations */
.banner-slide-enter-active,
.banner-slide-leave-active {
  transition: max-height 0.25s ease, opacity 0.25s ease;
  overflow: hidden;
}

.banner-slide-enter-from,
.banner-slide-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>
